import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FaBell } from "react-icons/fa";
import { formatDateTimeNoSeconds } from "../utils/dateFormatter";
import { API_BASE_URL, getAuthHeaderOnly } from "../utils/announcementApi";

async function fetchHasUnread() {
    const res = await fetch(`${API_BASE_URL}/get_announcement_has_unread`, {
        headers: getAuthHeaderOnly(),
    });
    if (!res.ok) throw new Error("Failed to fetch unread status");
    const json = await res.json();
    return Boolean(json.data?.has_unread);
}

async function fetchRecent() {
    const res = await fetch(`${API_BASE_URL}/get_announcement_recent`, {
        headers: getAuthHeaderOnly(),
    });
    if (!res.ok) throw new Error("Failed to fetch recent announcements");
    return res.json();
}

export default function AnnouncementBell() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const { data: hasUnread = false } = useQuery({
        queryKey: ["announcementHasUnread"],
        queryFn: fetchHasUnread,
        refetchInterval: 60000,
        refetchOnWindowFocus: true,
    });

    // ログイン後・機能画面遷移時にも未読状態を確認する
    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: ["announcementHasUnread"] });
    }, [location.pathname, queryClient]);

    const { data: recentData } = useQuery({
        queryKey: ["announcementRecent"],
        queryFn: fetchRecent,
        enabled: open,
    });

    const items = recentData?.data ?? [];
    const total = recentData?.total ?? 0;
    const showViewAll = total > 5;

    const closeDropdown = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (!open) return undefined;
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                closeDropdown();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open, closeDropdown]);

    const handleItemClick = (id) => {
        closeDropdown();
        navigate(`/dashboard/announcements/${id}`, { state: { from: location.pathname } });
    };

    const handleViewAll = () => {
        closeDropdown();
        navigate("/dashboard/announcements");
    };

    const handleBellClick = () => {
        setOpen((prev) => !prev);
        if (!open) {
            queryClient.invalidateQueries({ queryKey: ["announcementRecent"] });
            queryClient.invalidateQueries({ queryKey: ["announcementHasUnread"] });
        }
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={handleBellClick}
                className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Announcements"
                aria-label="Announcements"
            >
                <FaBell className="w-5 h-5 text-gray-700" />
                {hasUnread && (
                    <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-white" />
                )}
            </button>

            {open && (
                <div className="absolute top-full right-0 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-[80] overflow-hidden">
                    <div className="px-3 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-800">
                        Announcements
                    </div>
                    <ul className="max-h-80 overflow-y-auto">
                        {items.length === 0 && (
                            <li className="px-3 py-4 text-sm text-gray-500 text-center">No announcements</li>
                        )}
                        {items.map((item) => (
                            <li key={item.announcement_id}>
                                <button
                                    type="button"
                                    onClick={() => handleItemClick(item.announcement_id)}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 flex items-start gap-2"
                                >
                                    {!item.is_read && (
                                        <span className="mt-1.5 w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
                                    )}
                                    <div className={`min-w-0 flex-1 ${item.is_read ? "pl-4" : ""}`}>
                                        <div className={`text-sm truncate ${item.is_read ? "text-gray-700" : "font-semibold text-gray-900"}`}>
                                            {item.title}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {formatDateTimeNoSeconds(item.upd_date)}
                                        </div>
                                    </div>
                                </button>
                            </li>
                        ))}
                        {showViewAll && (
                            <li>
                                <button
                                    type="button"
                                    onClick={handleViewAll}
                                    className="w-full text-center px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 font-medium"
                                >
                                    View all
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
